/**
 * Copyright 2023-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useState } from "react";
import { RowSelectionState } from "@tanstack/table-core";
import { If, Then, Else } from "react-if";
import { Box, Tab, Tabs as TabContainer, Text, theme } from "@nordcloud/gnui";
import { NoData } from "~/components";
import { TableWrapper } from "~/components/Tables";
import { useTabStepNavigation } from "~/hooks";
import { useGNUITheme } from "~/hooks/useGNUITheme";
import { isEmpty, isNotNil } from "~/tools";
import { useResource } from "~/views/resources/ResourceProvider";
import { columns, KeyValue } from "./KeyValueColumns";
import { ResourceDetailsActionsTab } from "./ResourceDetailsActionsTab";
import { ResourceDetailsResourceGroupsTab } from "./ResourceDetailsResourceGroupsTab";
import { ScheduleTab } from "./ScheduleTab";

const TAB_STEP_MAPPING: Record<string, number> = {
  actions: 0,
  resource_groups: 1,
  schedule: 2,
  metadata: 3,
  tags: 4,
};

export function Tabs() {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const { currentStep, handleTab } = useTabStepNavigation(TAB_STEP_MAPPING);

  const { isLight } = useGNUITheme();

  const { resource } = useResource();
  const metadata =
    resource?.cloudMetadata
      ?.filter(isNotNil)
      .filter((item) => item.key.toLowerCase() !== "tags") ?? [];
  const tags = resource?.tags?.filter(isNotNil) ?? [];

  return (
    <Box spacing="spacing02" css={{ padding: 0 }}>
      <TabContainer step={currentStep} handleTab={handleTab}>
        <Tab heading="Actions">
          <Text mb={0}>Action History</Text>
          <Text size="sm" color={theme.color.text.text02}>
            List of actions applied to the resource.
          </Text>
          <ResourceDetailsActionsTab resourceId={resource?.id ?? ""} />
        </Tab>
        <Tab heading="Resource Groups">
          <Text mb={0}>Resource Groups</Text>
          <Text size="sm" color={theme.color.text.text02}>
            List of resource groups and plans where resource is used.
          </Text>
          <ResourceDetailsResourceGroupsTab
            resourceId={resource?.id ?? ""}
            resourceName={resource?.name ?? ""}
          />
        </Tab>
        <Tab heading="Schedule">
          <Text mb={0}>Schedule</Text>
          <Text size="sm" color={theme.color.text.text02}>
            Calendar view of past and upcoming actions scheduled for this
            resource.
          </Text>
          <ScheduleTab />
        </Tab>
        <Tab heading="Metadata">
          <Text mb={0}>Cloud Metadata</Text>
          <Text size="sm" color={theme.color.text.text02}>
            Cloud Metadata is descriptive information associated with cloud
            resources, providing details about attributes, configuration, and
            relationships.
          </Text>
          <If condition={isEmpty(metadata)}>
            <Then>
              <NoData hasIcon message={"There are no Metadata."} />
            </Then>
            <Else>
              <TableWrapper<Partial<KeyValue>>
                loading={false}
                data={metadata}
                columns={columns(isLight)}
                rowSelection={rowSelection}
                setRowSelection={setRowSelection}
                stripped
              />
            </Else>
          </If>
        </Tab>
        <Tab heading="Tags">
          <Text mb={0}>Tags</Text>
          <Text size="sm" color={theme.color.text.text02}>
            Tags are custom labels or identifiers used to categorize and
            organize resources for better management and resource grouping in a
            cloud computing environment.
          </Text>
          <If condition={isEmpty(tags)}>
            <Then>
              <NoData hasIcon message={"There are no Tags."} />
            </Then>
            <Else>
              <TableWrapper<Partial<KeyValue>>
                loading={false}
                data={tags}
                columns={columns(isLight)}
                rowSelection={rowSelection}
                setRowSelection={setRowSelection}
                stripped
              />
            </Else>
          </If>
        </Tab>
      </TabContainer>
    </Box>
  );
}
